/**
 * geluid-duur.js — hoe lang duurt een geluidsbestand? (in de browser)
 *
 * Een Worker kan audio niet decoderen zonder extra bibliotheek; de browser wel. Daarom meet de
 * backoffice de lengte van een scangeluid vóór het uploaden, en stuurt hij die mee. De server
 * weigert alles boven 3000 ms (zie CompanySoundsController).
 */
export const MAX_SCANGELUID_MS = 3000;
// 3 s wav in stereo is ~520 KB; daar moet het nog in passen.
export const MAX_SCANGELUID_BYTES = 600 * 1024;

/**
 * Lengte in ms, of null als de browser het bestand niet als geluid kan lezen.
 *
 * Eerst door het bestand te decoderen (precies). Lukt dat niet, dan via een <audio>-element:
 * een browser kan soms een bestand wel afspelen maar niet decoderen (bv. bepaalde m4a/aac-
 * varianten, of ogg in Safari). Dan is de lengte uit de metadata goed genoeg.
 */
export async function meetGeluidDuur(file) {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (Ctx) {
    const ctx = new Ctx();
    try {
      const buf = await ctx.decodeAudioData(await file.arrayBuffer());
      return Math.round(buf.duration * 1000);
    } catch {
      /* hieronder via <audio> */
    } finally {
      ctx.close?.();
    }
  }
  return duurViaAudioElement(file);
}

function duurViaAudioElement(file) {
  return new Promise((klaar) => {
    const url = URL.createObjectURL(file);
    const a = new Audio();
    let gedaan = false;
    const einde = (ms) => { if (gedaan) return; gedaan = true; clearTimeout(t); a.src = ''; URL.revokeObjectURL(url); klaar(ms); };
    const t = setTimeout(() => einde(null), 8000);
    a.preload = 'metadata';
    a.onerror = () => einde(null);
    a.onloadedmetadata = () => {
      if (Number.isFinite(a.duration) && a.duration > 0) return einde(Math.round(a.duration * 1000));
      // Opnames uit de browser (webm) melden soms 'Infinity'; naar het eind springen geeft de echte lengte.
      a.ondurationchange = () => { if (Number.isFinite(a.duration) && a.duration > 0) einde(Math.round(a.duration * 1000)); };
      a.currentTime = 1e7;
    };
    a.src = url;
  });
}

/** Waarom dit bestand niet als scangeluid kan, of null als het goed is. */
export function scangeluidProbleem({ type, size, duurMs }) {
  if (!/^audio\//.test(type || '')) return 'Kies een geluidsbestand (mp3, m4a, wav of ogg).';
  if (size > MAX_SCANGELUID_BYTES) return 'Het bestand is te groot (max 600 KB).';
  if (duurMs == null) return 'Dit bestand kan niet als geluid worden gelezen.';
  if (duurMs > MAX_SCANGELUID_MS) return `Dit geluid duurt ${(duurMs / 1000).toFixed(1).replace('.', ',')} seconden. Een scangeluid mag niet langer zijn dan 3 seconden.`;
  return null;
}
